
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import CompactTracking from './CompactTracking';

const TokenHistory = () => {
  const [tokens, setTokens] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [skippedCount, setSkippedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [tokensPerPage] = useState(10);
  const navigate = useNavigate();

  // Status mapping for token states
  const getStatusBadge = (status) => {
    switch (status) {
      case 1: return { text: 'Completed', class: 'bg-success' };
      case 2: return { text: 'Skipped', class: 'bg-warning text-dark' };
      case 3: return { text: 'Cancelled', class: 'bg-secondary' };
      default: return { text: 'Pending', class: 'bg-light text-dark' };
    }
  };

  // Memoize fetch function
  const fetchTokens = useCallback(async (page = 1) => {
    try {
      const token = localStorage.getItem("tokenly_token");
      if (!token) {
        setError("No authentication token found. Please login again.");
        navigate("/login");
        return;
      }

      setLoading(true);
      setError(null);
      
      const response = await fetch("https://tokenly.biswaas.com/api/user/tokenHistory", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          pageNo: page,
          limit: 10
        })
      });

      // Also fetch total counts for all statuses
      const statsResponse = await fetch("https://tokenly.biswaas.com/api/user/tokenHistory", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          pageNo: 1,
          limit: 1000000 // Large number to get all tokens for stats
        })
      });

      if (!response.ok || !statsResponse.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      const statsResult = await statsResponse.json();
      
      // Calculate total completed and skipped counts from all tokens
      let totalCompleted = 0;
      let totalSkipped = 0;
      
      if (statsResult.status && statsResult.data && statsResult.data.rows) {
        statsResult.data.rows.forEach((row) => {
          if (row.status === 1) totalCompleted++;
          else if (row.status === 2) totalSkipped++;
        });
      }
      
      setCompletedCount(totalCompleted);
      setSkippedCount(totalSkipped);
      
      if (result.status && result.data && result.data.rows) {
        const mappedTokens = result.data.rows
          .map((row) => ({
            id: row.id,
            tokenNo: row.token_no,
            patientName: row.name,
            doctorName: row.doctor.fullname,
            doctorId: row.doctor.id,
            doctorPhoto: row.doctor.profile_photo_full,
            status: row.status,
            statusBadge: getStatusBadge(row.status),
            shift: row.shift === 0 ? 'Morning' : row.shift === 1 ? 'Evening' : 'Other',
            shiftTime: row.shift_time,
            isRecall: row.is_recall === 1,
            isDefault: row.is_default === 1,
            createdAt: new Date(row.created_at).toLocaleDateString('en-IN'),
            updatedAt: new Date(row.updated_at).toLocaleDateString('en-IN'),
            amount: row.amount
          }));
        
        setTokens(mappedTokens);
        setTotalRows(result.data.total_rows || 0);
        setCurrentPage(page);
      } else {
        setTokens([]);
        setError(result.message || "No tokens found");
      }
    } catch (err) {
      setError(err.message);
      console.error('Token history API error:', err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchTokens(currentPage);
  }, [currentPage, fetchTokens]);

  const handleRefresh = () => {
    fetchTokens(1);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalRows / tokensPerPage);
const paginate = useCallback((pageNumber) => {
  if (pageNumber >= 1 && pageNumber <= totalPages) {
    setCurrentPage(pageNumber);  // Let useEffect handle the fetch
  }
}, [totalPages]);


  if (loading) {
    return (
      <div className="container mt-5">
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status" style={{width: '3rem', height: '3rem'}}>
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
        <p className="text-center mt-3 h6 text-muted">Loading your token history...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger text-center p-4">
          <i className="fas fa-exclamation-triangle fa-2x mb-3 d-block"></i>
          <h5>Error loading token history</h5>
          <p className="mb-4">{error}</p>
          <button className="btn btn-outline-danger me-2" onClick={handleRefresh}>
            <i className="fas fa-sync-alt me-1"></i>Retry
          </button>
          <button className="btn btn-outline-secondary" onClick={() => navigate("/")}>
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-light min-vh-100">
      {/* Hero Section with Background Image - Same as other pages */}
      <section className="contact-hero d-flex align-items-center">
        <div className="contact-hero-overlay"></div>
        <div className="container-fluid container-xl px-4 px-md-3">
          <h1 className="display-5 fw-bold text-white mb-1">Token <span className="text-warning">History</span></h1>
          <p className="text-light mb-0">
            All your booked tokens with doctors
          </p>
        </div>
      </section>
      <CompactTracking />

      <div className="container-fluid py-4">
        <div className="row justify-content-center">
          <div className="col-lg-11 col-xl-10">

          {/* Stats Cards */}
          {tokens.length > 0 && (
            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <div className="card border-0 shadow-sm bg-info text-white h-100">
                  <div className="card-body text-center">
                    <i className="bi bi-list-ul fa-3x mb-3"></i>
                    <h2 className="fw-bold mb-1">{totalRows.toLocaleString()}</h2>
                    <p className="mb-0 small fw-semibold">Total Tokens</p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card border-0 shadow-sm bg-success text-white h-100">
                  <div className="card-body text-center">
                    <i className="bi bi-check-circle fa-3x mb-3"></i>
                    <h2 className="fw-bold mb-1">{completedCount.toLocaleString()}</h2>
                    <p className="mb-0 small fw-semibold">Completed Tokens</p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card border-0 shadow-sm bg-warning text-dark h-100">
                  <div className="card-body text-center">
                    <i className="bi bi-skip-forward fa-3x mb-3"></i>
                    <h2 className="fw-bold mb-1">{skippedCount.toLocaleString()}</h2>
                    <p className="mb-0 small fw-semibold">Skipped Tokens</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          {tokens.length === 0 ? (
            <div className="text-center py-5 my-5">
              <i className="fas fa-ticket-alt fa-4x text-muted mb-4 opacity-50"></i>
              <h4 className="text-muted mb-3">No tokens booked yet</h4>
              <p className="text-muted mb-4 lead">
                Book your first token with a doctor to see history here.
              </p>
              <div className="d-flex justify-content-center gap-3 flex-wrap">
                <button className="btn btn-outline-primary" onClick={() => navigate("/alldoctors")}>
                  <i className="fas fa-search me-1"></i>Find Doctors
                </button>
                <button className="btn btn-primary" onClick={() => navigate("/wallet")}>
                  <i className="fas fa-wallet me-1"></i>Check Wallet
                </button>
              </div>
            </div>
          ) : (
            <div className="card shadow-lg border-0">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-dark sticky-top">
                      <tr>
                        <th className="ps-4 py-3">
                          Sr.No
                        </th>
                        <th className="ps-4 py-3">
                          Date Booked
                        </th>
                        <th className="py-3">Doctor Name</th>
                        <th className="py-3">Shift</th>
                        <th className="ps-4 py-3">
                          Token No
                        </th>
                        <th className="py-3">Token Status</th>
                      </tr>
                    </thead>
                    <tbody className="table-group-divider">
                      {tokens.map((token, index) => (
                        <tr key={token.id}>
                          <td className="ps-4 fw-medium text-muted">
                            {(currentPage - 1) * tokensPerPage + index + 1}
                          </td>
                          <td className="ps-4 fw-medium">
                            {token.createdAt}
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <img 
                                src={token.doctorPhoto} 
                                className="rounded-circle me-2" 
                                width="32" 
                                height="32" 
                                alt={token.doctorName}
                                onError={(e) => {
                                  e.target.src = '/img/no_image.gif';
                                }}
                              />
                              <div className="fw-semibold">{token.doctorName}</div>
                            </div>
                          </td>
                          <td>
                            <span className="badge bg-light text-dark px-2 py-1">
                              {token.shiftTime}
                            </span>
                          </td>
                          <td className="ps-4 fw-bold h5 text-primary">
                            {token.tokenNo}
                          </td>
                          <td>
                            <span className={`badge ${token.statusBadge.class} px-3 py-2 fs-6 fw-semibold`}>
                              {token.statusBadge.text}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Pagination Controls */}
              {totalRows > 0 && (
                <div className="card-footer bg-light border-0 py-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <button 
                      className="btn btn-primary btn-sm rounded-pill"
                      onClick={handleRefresh}
                      disabled={loading}
                    >
                      <i className="fas fa-sync-alt me-1"></i>
                      Refresh
                    </button>
                    <div className="btn-group" role="group">
                      <button 
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <i className="fas fa-chevron-left"></i> Previous
                      </button>
                      <span className="btn btn-outline-secondary btn-sm">
                        in record {(currentPage - 1) * tokensPerPage + 1}-{Math.min(currentPage * tokensPerPage, totalRows)}
                      </span>
                      <button 
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next <i className="fas fa-chevron-right"></i>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    </div>
  );
};

export default TokenHistory;
